import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { PV } from '@/constants/testIds';
import { ArrowRight, ShieldCheck, Loader2, CheckCircle2 } from 'lucide-react';
import { getLeadSource } from '@/lib/leadSource';
import { buildBridgeLead } from '@/lib/eventsPortal';
import ConsentChecklist from '@/components/site/ConsentChecklist';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  message: '',
  privacy_consent: false,
  email_newsletter_consent: false,
  whatsapp_newsletter_consent: false,
};

export const ContactForm = ({ compact = false }) => {
  const { t, locale } = useLanguage();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  const onChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = t.contact.required;
    if (!form.last_name.trim()) e.last_name = t.contact.required;
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t.contact.required;
    if (!form.phone.trim()) e.phone = t.contact.required;
    if (!form.privacy_consent) e.privacy_consent = t.contact.required;
    if (!form.email_newsletter_consent) e.email_newsletter_consent = t.contact.required;
    if (!form.whatsapp_newsletter_consent) e.whatsapp_newsletter_consent = t.contact.required;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error(t.contact.error);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bridge/contacts`, buildBridgeLead(form, 'contatti', {
        message: form.message,
        locale,
        source_detail: getLeadSource('landing-form'),
      }));
      setDone(true);
      setForm(initial);
      toast.dismiss();
      toast.success(t.contact.success);
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={PV.contactSection}
      className="pv-section-light relative py-24 md:py-32 border-t border-slate-200/60"
    >
      <div className="pv-glow-blob w-[520px] h-[520px] bg-pv-teal/12 top-[-160px] right-[-160px]" />
      <div className="pv-glow-blob w-[420px] h-[420px] bg-pv-orange/10 bottom-[-140px] left-[-140px]" />

      <div className="pv-container relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14 items-start">
          {/* Left intro (suppressed in compact mode) */}
          {!compact && (
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <span className="pv-eyebrow">{t.contact.eyebrow}</span>
            <h2 className="mt-5 font-heading font-bold tracking-normal text-[34px] sm:text-[42px] md:text-[50px] leading-[1.05] text-pv-ink">
              {t.contact.title_a}
              <br />
              <span className="pv-text-gradient">{t.contact.title_b}</span>
            </h2>
            <p className="mt-6 text-slate-600 text-base leading-relaxed max-w-md">
              {t.contact.lead}
            </p>

            <div className="mt-8 space-y-3">
              {[
                ['24h', 'Risposta media'],
                ['100%', 'Confidenziale · GDPR'],
                ['EU', 'Dati su server europei'],
              ].map(([k, v], i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <span className="font-heading font-bold text-pv-teal text-lg w-12">{k}</span>
                  <span className="text-slate-600 font-medium">{v}</span>
                </div>
              ))}
            </div>
          </div>
          )}

          {/* Form card */}
          <div className={compact ? 'lg:col-span-12 max-w-3xl mx-auto w-full' : 'lg:col-span-7'}>
            <form
              onSubmit={submit}
              className="relative bg-white rounded-3xl p-6 md:p-10 border border-slate-200 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.20)] overflow-hidden"
              noValidate
            >
              <div className="absolute -top-32 -right-20 w-64 h-64 rounded-full bg-pv-teal/12 blur-3xl pointer-events-none" />
              <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                <Field
                  id="first_name"
                  label={t.contact.firstName}
                  testId={PV.contactFirstName}
                  value={form.first_name}
                  onChange={onChange('first_name')}
                  error={errors.first_name}
                  required
                />
                <Field
                  id="last_name"
                  label={t.contact.lastName}
                  testId={PV.contactLastName}
                  value={form.last_name}
                  onChange={onChange('last_name')}
                  error={errors.last_name}
                  required
                />
                <Field
                  id="email"
                  label={t.contact.email}
                  type="email"
                  testId={PV.contactEmail}
                  value={form.email}
                  onChange={onChange('email')}
                  error={errors.email}
                  required
                />
                <Field
                  id="phone"
                  label={t.contact.phone}
                  type="tel"
                  testId={PV.contactPhone}
                  value={form.phone}
                  onChange={onChange('phone')}
                  error={errors.phone}
                  required
                />
                <div className="sm:col-span-2">
                  <label htmlFor="message" className="block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-2 font-bold">
                    {t.contact.message}
                  </label>
                  <textarea
                    id="message"
                    data-testid={PV.contactMessage}
                    rows={4}
                    placeholder={t.contact.messagePlaceholder}
                    value={form.message}
                    onChange={onChange('message')}
                    className="pv-input resize-none"
                  />
                </div>

                <div className="sm:col-span-2 mt-2">
                  <ConsentChecklist
                    form={form}
                    onChange={onChange}
                    errors={errors}
                    testIdPrefix="pv-contact"
                  />
                </div>
              </div>

              <div className="relative mt-8 flex flex-col sm:flex-row items-center gap-4">
                <button
                  type="submit"
                  data-testid={PV.contactSubmit}
                  disabled={submitting}
                  className="pv-btn-primary group w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t.contact.submitting}
                    </>
                  ) : (
                    <>
                      {t.contact.submit}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </button>

                <div className="flex items-center gap-2 text-[12.5px] text-slate-500 font-medium">
                  <ShieldCheck className="w-4 h-4 text-pv-teal" />
                  GDPR · crittografato end-to-end
                </div>
              </div>

              {done && (
                <div
                  data-testid={PV.contactSuccess}
                  className="relative mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-center gap-3 font-medium"
                >
                  <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600" />
                  {t.contact.success}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ id, label, type = 'text', testId, value, onChange, error, required }) => {
  return (
    <div className="flex flex-col">
      <label htmlFor={id} className="block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-2 font-bold">
        {label}{required && <span className="text-pv-orange ml-1">*</span>}
      </label>
      <input
        id={id}
        type={type}
        data-testid={testId}
        value={value}
        onChange={onChange}
        className={`pv-input ${error ? 'border-red-400' : ''}`}
      />
      {error && <p className="mt-1.5 text-[11.5px] text-red-500 font-semibold">{error}</p>}
    </div>
  );
};

export default ContactForm;
